/**
 * Typed-name widget, used to create a profile and to confirm deleting one.
 *
 * Typing the name is the point: it is the first typing practice a child gets
 * before the climb starts (CLAUDE.md).
 */
import Phaser from 'phaser';
import { MAX_NAME_LENGTH, sanitizeName } from '../../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../../types';
import { pressButton } from '../ui/menuButton';

export const NAME_ENTRY_DEPTH = 300;

export interface NameEntryOptions {
  readonly title: string;
  readonly hint: string;
  /** When set, Enter only fires onConfirm if the typed name matches this. */
  readonly mustMatch?: string;
  readonly confirmLabel: string;
  onConfirm(name: string): void;
  onCancel(): void;
}

export class NameEntry {
  private readonly container: Phaser.GameObjects.Container;
  private readonly entryText: Phaser.GameObjects.Text;
  private readonly statusText: Phaser.GameObjects.Text;
  private readonly caret: Phaser.GameObjects.Rectangle;
  private typed = '';
  private open = false;

  constructor(
    scene: Phaser.Scene,
    private readonly options: NameEntryOptions,
  ) {
    const dim = scene.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x0b1220,
      0.82,
    );

    const panel = scene.add.graphics();
    panel.fillStyle(0x16233a, 0.98);
    // Tall stage: narrower, and taller to hold OK / Cancel buttons, since a
    // phone has no Enter or Esc to hand. It stays above the device keyboard.
    const half = Math.min(380, GAME_WIDTH / 2 - 20);
    const panelH = IS_PORTRAIT ? 440 : 340;
    panel.fillRoundedRect(GAME_WIDTH / 2 - half, 190, half * 2, panelH, 28);
    panel.lineStyle(6, 0x8ecae6, 1);
    panel.strokeRoundedRect(GAME_WIDTH / 2 - half, 190, half * 2, panelH, 28);

    const title = scene.add
      .text(GAME_WIDTH / 2, 248, options.title, {
        fontFamily: FONT_FAMILY,
        fontSize: '42px',
        color: PALETTE.snow,
      })
      .setOrigin(0.5);

    const hint = scene.add
      .text(GAME_WIDTH / 2, 300, options.hint, {
        fontFamily: FONT_FAMILY,
        fontSize: '22px',
        color: PALETTE.ice,
        align: 'center',
        wordWrap: { width: Math.min(660, half * 2 - 60) },
      })
      .setOrigin(0.5);

    this.entryText = scene.add
      .text(GAME_WIDTH / 2, 386, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '54px',
        color: PALETTE.gold,
      })
      .setOrigin(0.5);

    this.caret = scene.add.rectangle(GAME_WIDTH / 2, 386, 5, 52, 0xffc145);

    this.statusText = scene.add
      .text(GAME_WIDTH / 2, 470, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '22px',
        color: PALETTE.snow,
      })
      .setOrigin(0.5);

    const buttons: Phaser.GameObjects.Text[] = [];
    if (IS_PORTRAIT) {
      const press = (key: string): void => {
        this.handleKey(new KeyboardEvent('keydown', { key }));
      };
      (
        [
          [
            options.confirmLabel.charAt(0).toUpperCase() + options.confirmLabel.slice(1),
            'Enter',
            -1,
          ],
          ['Cancel', 'Escape', 1],
        ] as const
      ).forEach(([label, key, side]) => {
        const button = scene.add
          .text(GAME_WIDTH / 2 + side * 140, 570, label, {
            fontFamily: FONT_FAMILY,
            fontSize: '28px',
            color: PALETTE.snow,
            backgroundColor: '#1b2a41',
            padding: { x: 22, y: 12 },
          })
          .setOrigin(0.5)
          .setInteractive({ useHandCursor: true });
        button.on('pointerup', () => pressButton(button, () => press(key)));
        buttons.push(button);
      });
    }

    this.container = scene.add
      .container(0, 0, [
        dim,
        panel,
        title,
        hint,
        this.entryText,
        this.caret,
        this.statusText,
        ...buttons,
      ])
      .setDepth(NAME_ENTRY_DEPTH)
      .setScrollFactor(0)
      .setVisible(false);

    scene.tweens.add({
      targets: this.caret,
      alpha: { from: 1, to: 0.1 },
      duration: 480,
      yoyo: true,
      repeat: -1,
    });

    this.refresh();
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(): void {
    this.typed = '';
    this.open = true;
    this.refresh();
    this.container.setVisible(true);
  }

  hide(): void {
    this.open = false;
    this.container.setVisible(false);
  }

  /** Returns true when the key was consumed by this widget. */
  handleKey(event: KeyboardEvent): boolean {
    if (!this.open) return false;

    if (event.key === 'Escape') {
      this.hide();
      this.options.onCancel();
      return true;
    }

    if (event.key === 'Backspace') {
      this.typed = this.typed.slice(0, -1);
      this.refresh();
      return true;
    }

    if (event.key === 'Enter') {
      const name = sanitizeName(this.typed);
      if (this.canConfirm(name)) {
        this.hide();
        this.options.onConfirm(name);
      }
      return true;
    }

    // Letters and spaces only, which is also what sanitizeName keeps.
    if (/^[a-zA-Z ]$/.test(event.key) && this.typed.length < MAX_NAME_LENGTH) {
      this.typed += event.key;
      this.refresh();
      return true;
    }

    return true;
  }

  private canConfirm(name: string): boolean {
    if (name === '') return false;
    const required = this.options.mustMatch;
    if (required === undefined) return true;
    return name.toLowerCase() === sanitizeName(required).toLowerCase();
  }

  private refresh(): void {
    this.entryText.setText(this.typed);
    this.caret.setX(GAME_WIDTH / 2 + this.entryText.width / 2 + 10);

    const name = sanitizeName(this.typed);
    const ready = this.canConfirm(name);
    // The tall layout has buttons for these, and no Enter or Esc keys.
    this.statusText.setText(
      IS_PORTRAIT
        ? this.typed === ''
          ? 'Tap here to type'
          : ready
            ? 'Looks good!'
            : 'Keep typing...'
        : ready
          ? `Press Enter to ${this.options.confirmLabel}      Esc to go back`
          : 'Keep typing...      Esc to go back',
    );
    this.statusText.setColor(ready ? PALETTE.teal : PALETTE.ice);
  }

  destroy(): void {
    this.container.destroy();
  }
}
